import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  createCustomerKycPersonApi,
  deleteCustomerKycPersonApi,
  getCustomerKycApi,
  KYC_RENEWAL_UNIT_OPTIONS,
  listCustomerKycEventsApi,
  recordCustomerKycCompletionApi,
  updateCustomerKycPersonApi,
  updateCustomerKycSettingsApi,
} from 'api/customerKycApi';
import { TwDatePicker } from 'components/DatePicker';
import Card from 'components/card/Card';
import { DestructiveDialog } from 'components/Dialog';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { MdDelete, MdEdit, MdHistory } from 'react-icons/md';
import validator from 'validator';

const statusColor = (status) => {
  switch (status) {
    case 'current':
      return 'green';
    case 'overdue':
      return 'red';
    default:
      return 'red';
  }
};

const statusLabel = (status) => {
  switch (status) {
    case 'current':
      return 'Current';
    case 'overdue':
      return 'Overdue';
    default:
      return 'Never done';
  }
};

const formatDate = (value) =>
  value ? moment(value).format('YYYY-MM-DD') : '—';

function CustomerKycCard({ customerId, toast }) {
  const [loading, setLoading] = useState(true);
  const [kyc, setKyc] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState({
    renewalIntervalValue: 1,
    renewalIntervalUnit: 'years',
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const personModal = useDisclosure();
  const completionModal = useDisclosure();
  const historyModal = useDisclosure();

  const [personDraft, setPersonDraft] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [editingPerson, setEditingPerson] = useState(null);
  const [completionPerson, setCompletionPerson] = useState(null);
  const [completionDate, setCompletionDate] = useState(new Date());
  const [historyPerson, setHistoryPerson] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [personToDelete, setPersonToDelete] = useState(null);
  const [savingPerson, setSavingPerson] = useState(false);
  const [savingCompletion, setSavingCompletion] = useState(false);

  const loadKyc = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCustomerKycApi(customerId);
      setKyc(data);
      setSettingsDraft({
        renewalIntervalValue: data?.settings?.renewalIntervalValue ?? 1,
        renewalIntervalUnit: data?.settings?.renewalIntervalUnit ?? 'years',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to load KYC',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, toast]);

  useEffect(() => {
    loadKyc();
  }, [loadKyc]);

  const onSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const data = await updateCustomerKycSettingsApi(customerId, settingsDraft);
      setKyc(data);
      toast({ title: 'KYC renewal policy saved', status: 'success' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to save KYC settings',
        status: 'error',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const openAddPerson = () => {
    setEditingPerson(null);
    setPersonDraft({ firstName: '', lastName: '', email: '' });
    personModal.onOpen();
  };

  const openEditPerson = (person) => {
    setEditingPerson(person);
    setPersonDraft({
      firstName: person.firstName || '',
      lastName: person.lastName || '',
      email: person.email || '',
    });
    personModal.onOpen();
  };

  const onSavePerson = async () => {
    const firstName = personDraft.firstName.trim();
    const lastName = personDraft.lastName.trim();
    const email = personDraft.email.trim();
    if (!firstName || !lastName || !email || !validator.isEmail(email)) {
      toast({
        title: 'Invalid person',
        description: 'First name, last name, and valid email are required',
        status: 'error',
      });
      return;
    }
    setSavingPerson(true);
    try {
      if (editingPerson) {
        await updateCustomerKycPersonApi(customerId, editingPerson.id, {
          firstName,
          lastName,
          email,
        });
      } else {
        await createCustomerKycPersonApi(customerId, {
          firstName,
          lastName,
          email,
        });
      }
      personModal.onClose();
      await loadKyc();
      toast({ title: 'Person saved', status: 'success' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to save person',
        status: 'error',
      });
    } finally {
      setSavingPerson(false);
    }
  };

  const onDeletePerson = async () => {
    if (!personToDelete) return;
    try {
      await deleteCustomerKycPersonApi(customerId, personToDelete.id);
      setPersonToDelete(null);
      await loadKyc();
      toast({ title: 'Person removed', status: 'success' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to remove person',
        status: 'error',
      });
    }
  };

  const openRecordCompletion = (person) => {
    setCompletionPerson(person);
    setCompletionDate(new Date());
    completionModal.onOpen();
  };

  const onRecordCompletion = async () => {
    if (!completionPerson || !completionDate) return;
    setSavingCompletion(true);
    try {
      await recordCustomerKycCompletionApi(
        customerId,
        completionPerson.id,
        moment(completionDate).format('YYYY-MM-DD')
      );
      completionModal.onClose();
      await loadKyc();
      toast({ title: 'KYC completion recorded', status: 'success' });
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.error || 'Failed to record KYC completion',
        status: 'error',
      });
    } finally {
      setSavingCompletion(false);
    }
  };

  const openHistory = async (person) => {
    setHistoryPerson(person);
    setHistoryEvents([]);
    historyModal.onOpen();
    setHistoryLoading(true);
    try {
      const events = await listCustomerKycEventsApi(customerId, person.id);
      setHistoryEvents(events || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to load KYC history',
        status: 'error',
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) {
    return (
      <Card mt={6}>
        <Flex justify="center" py={8}>
          <Spinner />
        </Flex>
      </Card>
    );
  }

  const people = kyc?.people || [];

  return (
    <>
      <Card mt={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading variant="sectionHeader" borderColor="secondaryGray.600">
            KYC
          </Heading>
          {kyc?.summaryOk ? (
            <Badge colorScheme="green">Current</Badge>
          ) : (
            <Badge colorScheme="red">{kyc?.summaryLabel || 'No one configured'}</Badge>
          )}
        </Flex>

        <Box mb={6}>
          <Text fontSize="sm" color="secondaryGray.600" mb={3}>
            Renewal required every
          </Text>
          <HStack align="flex-end" spacing={3}>
            <FormControl maxW="120px">
              <FormLabel fontSize="sm">Interval</FormLabel>
              <Input
                type="number"
                min={1}
                value={settingsDraft.renewalIntervalValue}
                onChange={(e) =>
                  setSettingsDraft((prev) => ({
                    ...prev,
                    renewalIntervalValue: Number(e.target.value),
                  }))
                }
              />
            </FormControl>
            <FormControl maxW="160px">
              <FormLabel fontSize="sm">Unit</FormLabel>
              <Select
                value={settingsDraft.renewalIntervalUnit}
                onChange={(e) =>
                  setSettingsDraft((prev) => ({
                    ...prev,
                    renewalIntervalUnit: e.target.value,
                  }))
                }
              >
                {KYC_RENEWAL_UNIT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="brand"
              isLoading={savingSettings}
              onClick={onSaveSettings}
            >
              Save policy
            </Button>
          </HStack>
        </Box>

        <Flex justify="flex-end" mb={3}>
          <Button variant="brand" onClick={openAddPerson}>
            Add person
          </Button>
        </Flex>

        {people.length === 0 ? (
          <Text fontSize="sm" color="secondaryGray.500">
            No KYC people added yet.
          </Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>First name</Th>
                <Th>Last name</Th>
                <Th>Email</Th>
                <Th>Last KYC</Th>
                <Th>Next due</Th>
                <Th>Status</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {people.map((person) => (
                <Tr key={person.id}>
                  <Td>{person.firstName}</Td>
                  <Td>{person.lastName}</Td>
                  <Td>{person.email}</Td>
                  <Td>{formatDate(person.lastCompletedAt)}</Td>
                  <Td>{formatDate(person.nextDueAt)}</Td>
                  <Td>
                    <Badge colorScheme={statusColor(person.status)}>
                      {statusLabel(person.status)}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1} justify="flex-end">
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => openRecordCompletion(person)}
                      >
                        Record KYC
                      </Button>
                      <IconButton
                        aria-label="View KYC history"
                        icon={<MdHistory />}
                        size="sm"
                        variant="ghost"
                        onClick={() => openHistory(person)}
                      />
                      <IconButton
                        aria-label="Edit person"
                        icon={<MdEdit />}
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditPerson(person)}
                      />
                      <IconButton
                        aria-label="Delete person"
                        icon={<MdDelete />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => setPersonToDelete(person)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Card>

      <Modal isOpen={personModal.isOpen} onClose={personModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingPerson ? 'Edit KYC person' : 'Add KYC person'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>First name</FormLabel>
                <Input
                  value={personDraft.firstName}
                  onChange={(e) =>
                    setPersonDraft((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Last name</FormLabel>
                <Input
                  value={personDraft.lastName}
                  onChange={(e) =>
                    setPersonDraft((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={personDraft.email}
                  onChange={(e) =>
                    setPersonDraft((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={personModal.onClose}>
              Cancel
            </Button>
            <Button variant="brand" isLoading={savingPerson} onClick={onSavePerson}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={completionModal.isOpen} onClose={completionModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Record KYC completion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              {completionPerson
                ? `${completionPerson.firstName} ${completionPerson.lastName}`
                : ''}
            </Text>
            <FormControl isRequired>
              <FormLabel>Completion date</FormLabel>
              <TwDatePicker
                value={completionDate}
                onChange={setCompletionDate}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={completionModal.onClose}>
              Cancel
            </Button>
            <Button
              variant="brand"
              isLoading={savingCompletion}
              onClick={onRecordCompletion}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={historyModal.isOpen}
        onClose={historyModal.onClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            KYC history
            {historyPerson
              ? ` — ${historyPerson.firstName} ${historyPerson.lastName}`
              : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {historyLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : historyEvents.length === 0 ? (
              <Text fontSize="sm" color="secondaryGray.500">
                No KYC completions recorded yet.
              </Text>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Completed</Th>
                    <Th>Recorded</Th>
                    <Th>Source</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {historyEvents.map((event) => (
                    <Tr key={event.id}>
                      <Td>{formatDate(event.completedAt)}</Td>
                      <Td>{moment(event.recordedAt).format('LLL')}</Td>
                      <Td>{event.source || 'manual'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={historyModal.onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <DestructiveDialog
        show={Boolean(personToDelete)}
        header="Remove KYC person"
        body={`Remove ${personToDelete?.firstName || ''} ${personToDelete?.lastName || ''}?`}
        onDelete={onDeletePerson}
        onDialogClose={() => setPersonToDelete(null)}
        deleteButtonText="Remove"
      />
    </>
  );
}

CustomerKycCard.propTypes = {
  customerId: PropTypes.string.isRequired,
  toast: PropTypes.object.isRequired,
};

export default CustomerKycCard;
